import styled from 'styled-components'
import { incidentReportsConstants } from 'locale/incidentReports'
import { containsImageExtension, containsVideoExtension } from '../../utils'
import { CloudDownloadOutlined } from '@ant-design/icons'
import DownloadFile from '../DownloadFile'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import VideoDisplay from './VideoDisplay'

type AttachmentItem =
  | string
  | { url: string; thumbnail?: string | null; isVideo?: boolean }

type AttachmentsRowProps = {
  attachments: AttachmentItem[]
  testId?: string
}

const isVideo = (item: AttachmentItem, url: string): boolean => {
  if (typeof item === 'object' && item.isVideo) {
    return true
  }
  return containsVideoExtension(url)
}

const AttachmentsRow = ({ attachments, testId }: AttachmentsRowProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  return (
    <AttachmentsFlexRow>
      {attachments.map((item, index) => {
        const url = typeof item === 'string' ? item : item.url

        if (isVideo(item, url)) {
          const videoThumbnail =
            typeof item === 'object' ? item.thumbnail : null
          return (
            <VideoDisplay
              key={index}
              videoFile={url}
              videoThumbnail={videoThumbnail}
              testId={testId ? `${testId}-video-${index}` : undefined}
            />
          )
        }

        return containsImageExtension(url) ? (
          <DownloadFile key={index} fileName="file" fileUrl={url}>
            <AttachmentContainer>
              <ImageAttachment src={url} alt={`attached image ${index + 1}`} />
              <StyledCloudDownloadOutlined />
            </AttachmentContainer>
          </DownloadFile>
        ) : (
          <DownloadFile key={index} fileName="file" fileUrl={url}>
            <FileAttachment>
              <CloudDownloadOutlined />
              {t(incidentReportsConstants.FILE)}
            </FileAttachment>
          </DownloadFile>
        )
      })}
    </AttachmentsFlexRow>
  )
}

const AttachmentsFlexRow = styled.div`
  display: flex;
  gap: 8px;
  margin: 4px 0;
`

const AttachmentContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 58px;
  height: 72px;
  border-radius: 9px;
  &:hover {
    img {
      opacity: 0.3;
    }
    svg {
      visibility: visible;
    }
  }
`

const ImageAttachment = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 9px;
  object-fit: cover;
`

const StyledCloudDownloadOutlined = styled(CloudDownloadOutlined)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  visibility: hidden;
`

const FileAttachment = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 58px;
  height: 72px;
  border-radius: 9px;
  object-fit: cover;
  background-color: ${({ theme }) => theme.color.neutral.color9};
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  &:hover {
    background-color: ${({ theme }) => theme.color.neutral.color10};
  }
`

export default AttachmentsRow
